import React from 'react';

import { formatLinkBlockToUrl } from '$utilities/format';

import {
    IconLinkedIn,
    IconX
} from '$partials/icons';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/global.d.ts').Footer}
 */
const Footer = ({
    copyrightNavigation = '',
    copyrightText = '',
    ctaLabel = '',
    ctaLink = null,
    exploreColour = '',
    exploreHeading = '',
    exploreHide = false,
    exploreTags = null,
    exploreTitle = '',
    linkedinUrl = '',
    logo = null,
    magazineCtaCssClass = '',
    magazineColour = '',
    magazineCtaLabel = '',
    magazineCtaLink = null,
    magazineHeading = '',
    magazineHide = false,
    magazineImage = null,
    magazineTitle = '',
    navigationColumn1 = '',
    navigationColumn2 = '',
    navigationColumn3 = '',
    newsletterCtaCssClass = '',
    newsletterColour = '',
    newsletterCtaLabel = '',
    newsletterCtaLink = null,
    newsletterHeading = '',
    newsletterHide = false,
    newsletterTitle = '',
    pageType = '',
    xUrl = ''
}) => {
    // misc
    const splitColClass = (exploreHide || magazineHide) ? ' w-full' : ' w-full sm:w-6/12';

    return (
        <footer>
            <div className='layout-container layout-site-outer-padding'>
                <div className='col-wrapper-breakout flex flex-wrap gap-y-8 md:gap-y-16'>
                    {/* Explore */}
                    {!exploreHide && (
                        <div className={`sm:pr-4 md:pr-8${splitColClass}`}>
                            <div className={`h-full flex flex-col gap-24 md:gap-40 col-padding pb-8 md:pb-16 pt-24 rounded-lg overflow-hidden bg-surface-panel-${exploreColour}`}>
                                <div className='flex flex-col gap-8'>
                                    {exploreTitle && (
                                        <h2 className='type-style-sans-h1'>
                                            {exploreTitle}
                                        </h2>
                                    )}

                                    {exploreHeading && (
                                        <p className='w-5/6 type-style-sans-h3'>
                                            {exploreHeading}
                                        </p>
                                    )}
                                </div>

                                {exploreTags?.edges?.length > 0 && (
                                    <div>
                                        <ul className='flex flex-wrap gap-10'>
                                            {exploreTags.edges.map((itm, idx) => {
                                                if (!itm.node) {
                                                    return null;
                                                }

                                                return (
                                                    <li key={idx}>
                                                        <a
                                                            className='button-outcome'
                                                            href={itm.node.url}>
                                                            {itm.node.title}
                                                        </a>
                                                    </li>
                                                );
                                            }).filter(Boolean)}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Magazine */}
                    {!magazineHide && (
                        <div className={`sm:pl-4 md:pl-8${splitColClass}`}>
                            <div
                                className={`h-full flex flex-col gap-24 md:gap-40 col-padding pb-8 md:pb-16 pt-24 rounded-lg overflow-hidden bg-no-repeat bg-cover bg-right-bottom bg-surface-panel-${magazineColour}`}
                                style={{ backgroundImage: `url(${magazineImage?.px2})` }}>
                                <div className='flex flex-col gap-8'>
                                    {magazineTitle && (
                                        <h2 className='type-style-sans-h1'>
                                            {magazineTitle}
                                        </h2>
                                    )}

                                    {magazineHeading && (
                                        <p className='w-5/6 type-style-sans-h3'>
                                            {magazineHeading}
                                        </p>
                                    )}
                                </div>

                                {magazineCtaLabel && (
                                    <div>
                                        <a
                                            className={`button-primary${magazineCtaCssClass ? ' ' + magazineCtaCssClass : ''}`}
                                            href={formatLinkBlockToUrl(magazineCtaLink)}>
                                            {magazineCtaLabel}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Newsletter */}
                    {(!newsletterHide && (newsletterTitle || newsletterHeading || newsletterCtaLabel)) && (
                        <div className='w-full'>
                            <div className={`h-full flex flex-col gap-24 md:gap-40 col-padding pb-8 md:pb-16 pt-24 rounded-lg overflow-hidden bg-surface-panel-${newsletterColour}`}>
                                <div className='flex flex-col gap-8'>
                                    {newsletterTitle && (
                                        <h2 className='w-full sm:w-6/12 type-style-sans-h1'>
                                            {newsletterTitle}
                                        </h2>
                                    )}

                                    {newsletterHeading && (
                                        <p className='w-full sm:w-5/6 type-style-sans-h3'>
                                            {newsletterHeading}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <a
                                        className={`button-primary${newsletterCtaCssClass ? ' ' + newsletterCtaCssClass : ''}`}
                                        href={formatLinkBlockToUrl(newsletterCtaLink)}>
                                        {newsletterCtaLabel}
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className='col-wrapper flex flex-wrap justify-between pb-16 pt-48'>
                    <div className='w-11/12 sm:w-3/12 col-padding flex flex-col gap-24'>
                        {/* Logo */}
                        {(logo && pageType === 'Home') && (
                            <img
                                className='w-full pointer-events-none select-none'
                                height={logo.height}
                                loading='eager'
                                role='presentation'
                                src={logo.px2}
                                width={logo.width} />
                        )}

                        {(logo && pageType !== 'Home') && (
                            <a
                                aria-label='Back to homepage'
                                href='/'>
                                <img
                                    className='w-full pointer-events-none select-none'
                                    height={logo.height}
                                    loading='eager'
                                    role='presentation'
                                    src={logo.px2}
                                    width={logo.width} />
                            </a>
                        )}

                        {/* Social */}
                        <ul className='flex gap-8'>
                            {/* CTA */}
                            {(ctaLink && ctaLabel) && (
                                <div>
                                    <a
                                        className='button-outcome-xl w-full justify-start'
                                        href={formatLinkBlockToUrl(ctaLink)}>
                                        {ctaLabel}
                                    </a>
                                </div>
                            )}

                            {linkedinUrl && (
                                <li>
                                    <a
                                        aria-label='Linked In'
                                        className='button-icon-outcome-xl'
                                        href={linkedinUrl}
                                        rel='noreferrer'
                                        target='_blank'>
                                        <IconLinkedIn />
                                    </a>
                                </li>
                            )}

                            {xUrl && (
                                <li>
                                    <a
                                        aria-label='X'
                                        className='button-icon-outcome-xl'
                                        href={xUrl}
                                        rel='noreferrer'
                                        target='_blank'>
                                        <IconX />
                                    </a>
                                </li>
                            )}
                        </ul>
                    </div>

                    {/* Navigation */}
                    <nav
                        className='w-full sm:w-6/12 pt-48 sm:pt-0 flex flex-wrap gap-y-16 rich-text-footer'
                        id='secondary-navigation'>
                        {navigationColumn1 && (
                            <div
                                className='w-full sm:w-2/6 col-padding'
                                dangerouslySetInnerHTML={{ __html: navigationColumn1 }} />
                        )}

                        {navigationColumn2 && (
                            <div
                                className='w-full sm:w-2/6 col-padding'
                                dangerouslySetInnerHTML={{ __html: navigationColumn2 }} />
                        )}

                        {navigationColumn3 && (
                            <div
                                className='w-full sm:w-2/6 col-padding'
                                dangerouslySetInnerHTML={{ __html: navigationColumn3 }} />
                        )}
                    </nav>
                </div>

                <div className='col-wrapper flex flex-wrap justify-between pb-16 pt-48'>
                    {/* Copyright Navigation */}
                    <div className='w-full sm:w-6/12 col-padding order-1 sm:order-2'>
                        <nav
                            className='type-style-sans-body text-text-default-secondary col-wrapper flex flex-wrap gap-y-16 rich-text-copyright'
                            dangerouslySetInnerHTML={{ __html: copyrightNavigation }}
                            id='legal-navigation' />
                    </div>

                    {/* Copyright Text */}
                    <div className='w-full sm:w-5/12 col-padding order-2 sm:order-1'>
                        <div
                            className='pt-16 md:pt-0 type-style-sans-body text-text-default-secondary'
                            dangerouslySetInnerHTML={{ __html: copyrightText }} />
                    </div>
                </div>
            </div>
        </footer>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default Footer;
